import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Checkbox, Collapse, Empty, Tooltip } from 'antd';
import { SearchOutlined, ExpandOutlined, CompressOutlined, CheckSquareOutlined, MinusSquareOutlined } from '@ant-design/icons';
import { apiClient, apiErrorMessage } from '../../../utils';
import { PermissionTargetGroup, PermissionTarget, FilterGroup } from '../../../models';

// Type guard to check if a child is a PermissionTarget (has a key property)
const isPermissionTarget = (child: PermissionTarget | PermissionTargetGroup): child is PermissionTarget => {
  return 'key' in child;
};

// Type guard to check if a child is a PermissionTargetGroup (has children but no key)
const isPermissionTargetGroup = (child: PermissionTarget | PermissionTargetGroup): child is PermissionTargetGroup => {
  return 'children' in child && !('key' in child);
};

export interface SelectedPermission {
  id?: string; // For existing permissions when editing
  target: string;
  label: string;
  effect: 'ALLOW' | 'DENY';
  filter?: FilterGroup;
  filterEnabled?: boolean;
  conditionSchemes: string[] | null;
  dataSetId?: string;
  isNew?: boolean; // True if permission was just added and not yet saved
  isDeleted?: boolean; // True if permission was marked for deletion
}

interface TargetSelectorProps {
  onPermissionsChange: (permissions: Map<string, SelectedPermission>) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  expandedKeys: string[];
  onExpandedKeysChange: (keys: string[]) => void;
  totalPermissionCount?: number;
  roleId?: string;
  currentPermissions?: Map<string, SelectedPermission>; // Current permissions to preserve on modal reopen
}

export function TargetSelector(props: TargetSelectorProps) {
  const {
    onPermissionsChange,
    searchQuery,
    onSearchQueryChange,
    expandedKeys,
    onExpandedKeysChange,
    totalPermissionCount,
    roleId,
    currentPermissions,
  } = props;

  const [loadingPermissionTargets, setLoadingPermissionTargets] = useState(false);
  const [permissionTargets, setPermissionTargets] = useState<PermissionTargetGroup[]>([]);
  const [currentSelections, setCurrentSelections] = useState<Map<string, SelectedPermission>>(new Map());

  const displayedPermissionCount = Math.max(totalPermissionCount || 0, currentSelections.size);

  // Load permission targets on mount
  useEffect(() => {
    setLoadingPermissionTargets(true);
    apiClient
      .get('authorization/permission-targets', { params: roleId ? { roleId } : undefined })
      .then((response: any) => {
        setPermissionTargets(response.data);

        // Start with currentPermissions if provided (preserves ids, deletions, etc)
        const initialSelections = currentPermissions && currentPermissions.size > 0
          ? new Map<string, SelectedPermission>(currentPermissions)
          : new Map<string, SelectedPermission>();

        // Then add any backend-selected permissions that aren't already in the map
        const collectSelectedPermissions = (groups: PermissionTargetGroup[]) => {
          groups.forEach(group => {
            if (group.children) {
              group.children.forEach(child => {
                if (isPermissionTarget(child) && child.isSelected && !initialSelections.has(child.key)) {
                  // Only add if not already present
                  initialSelections.set(child.key, {
                    target: child.key,
                    label: child.label,
                    effect: 'ALLOW',
                    conditionSchemes: child.conditionSchemes || null,
                    dataSetId: child.dataSetId,
                    isNew: false,
                  });
                } else if (isPermissionTargetGroup(child)) {
                  collectSelectedPermissions([child]);
                }
              });
            }
          });
        };
        collectSelectedPermissions(response.data);

        setCurrentSelections(initialSelections);
        onPermissionsChange(initialSelections);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoadingPermissionTargets(false);
      });
  }, [roleId]);

  // Count selected permissions in a group (excludes deleted)
  const countSelectedInGroup = (group: PermissionTargetGroup): number => {
    let count = 0;

    const countInGroup = (g: PermissionTargetGroup): void => {
      if (g.children) {
        for (const child of g.children) {
          if (isPermissionTarget(child)) {
            if (isPermissionSelected(child.key)) {
              count++;
            }
          } else if (isPermissionTargetGroup(child)) {
            countInGroup(child);
          }
        }
      }
    };

    countInGroup(group);
    return count;
  };

  // Get filtered permission targets based on search
  const filteredPermissionTargets = useMemo(() => {
    if (!searchQuery) return permissionTargets;

    // Filter targets based on search query
    const matchesSearch = (text: string, query: string): boolean => {
      return text.toLowerCase().includes(query.toLowerCase());
    };

    const filterGroup = (group: PermissionTargetGroup, query: string): PermissionTargetGroup | null => {
      if (!query) return group;

      const filteredChildren: (PermissionTarget | PermissionTargetGroup)[] = [];

      if (group.children) {
        for (const child of group.children) {
          if (isPermissionTarget(child)) {
            if (matchesSearch(child.label, query) || matchesSearch(child.key, query)) {
              filteredChildren.push(child);
            }
          } else if (isPermissionTargetGroup(child)) {
            const filteredSubGroup = filterGroup(child, query);
            if (filteredSubGroup && filteredSubGroup.children && filteredSubGroup.children.length > 0) {
              filteredChildren.push(filteredSubGroup);
            }
          }
        }
      }

      if (filteredChildren.length === 0 && !matchesSearch(group.label, query)) {
        return null;
      }

      return {
        ...group,
        children: filteredChildren,
      };
    };

    const filtered: PermissionTargetGroup[] = [];
    for (const group of permissionTargets) {
      const filteredGroup = filterGroup(group, searchQuery);
      if (filteredGroup && filteredGroup.children && filteredGroup.children.length > 0) {
        filtered.push(filteredGroup);
      }
    }
    return filtered;
  }, [permissionTargets, searchQuery]);

  // Auto-expand all when searching
  useEffect(() => {
    if (searchQuery && filteredPermissionTargets.length > 0) {
      const allKeys: string[] = [];
      const collectKeys = (group: PermissionTargetGroup, path: string[] = []) => {
        const currentPath = [...path, group.label];
        const key = currentPath.join('/');
        allKeys.push(key);

        if (group.children) {
          group.children.forEach(child => {
            if (isPermissionTargetGroup(child)) {
              collectKeys(child, currentPath);
            }
          });
        }
      };

      filteredPermissionTargets.forEach(group => collectKeys(group));
      onExpandedKeysChange(allKeys);
    }
  }, [searchQuery, filteredPermissionTargets, onExpandedKeysChange]);

  // Handle target selection
  const handleTargetSelection = (target: PermissionTarget, checked: boolean) => {
    const newPermissions = new Map(currentSelections);
    const existing = newPermissions.get(target.key);

    if (checked) {
      // Check/select the permission
      if (existing) {
        // If exists but deleted, undelete it
        newPermissions.set(target.key, { ...existing, isDeleted: false });
      } else {
        // Add as new
        newPermissions.set(target.key, {
          target: target.key,
          label: target.label,
          effect: 'ALLOW',
          conditionSchemes: target.conditionSchemes || null,
          dataSetId: target.dataSetId,
          isNew: true,
        });
      }
    } else {
      // Uncheck/deselect the permission
      if (existing) {
        // If it's a backend permission (has id), mark as deleted instead of removing
        if (existing.id) {
          newPermissions.set(target.key, { ...existing, isDeleted: true });
        } else {
          // If it's a new unsaved permission, remove it
          newPermissions.delete(target.key);
        }
      }
    }

    setCurrentSelections(newPermissions);
    onPermissionsChange(newPermissions);
  };

  // Select all targets in a group
  const selectAllInGroup = (group: PermissionTargetGroup) => {
    const newSelections = new Map(currentSelections);

    const selectInGroup = (g: PermissionTargetGroup) => {
      if (g.children) {
        g.children.forEach(child => {
          if (isPermissionTarget(child)) {
            if (!newSelections.has(child.key)) {
              newSelections.set(child.key, {
                target: child.key,
                label: child.label,
                effect: 'ALLOW',
                conditionSchemes: child.conditionSchemes,
                dataSetId: child.dataSetId,
                isNew: true,
              });
            }
          } else if (isPermissionTargetGroup(child)) {
            selectInGroup(child);
          }
        });
      }
    };

    selectInGroup(group);
    setCurrentSelections(newSelections);
    onPermissionsChange(newSelections);
  };

  // Deselect all targets in a group
  const deselectAllInGroup = (group: PermissionTargetGroup) => {
    const newSelections = new Map(currentSelections);

    const deselectInGroup = (g: PermissionTargetGroup) => {
      if (g.children) {
        g.children.forEach(child => {
          if (isPermissionTarget(child)) {
            newSelections.delete(child.key);
          } else if (isPermissionTargetGroup(child)) {
            deselectInGroup(child);
          }
        });
      }
    };

    deselectInGroup(group);
    setCurrentSelections(newSelections);
    onPermissionsChange(newSelections);
  };

  // Helper to check if a permission is selected (exists and not deleted)
  const isPermissionSelected = (targetKey: string): boolean => {
    const permission = currentSelections.get(targetKey);
    return !!permission && !permission.isDeleted;
  };

  // Expand all groups
  const expandAll = () => {
    const allKeys: string[] = [];

    const collectKeys = (group: PermissionTargetGroup, path: string[] = []) => {
      const currentPath = [...path, group.label];
      const key = currentPath.join('/');
      allKeys.push(key);

      if (group.children) {
        group.children.forEach(child => {
          if (isPermissionTargetGroup(child)) {
            collectKeys(child, currentPath);
          }
        });
      }
    };

    permissionTargets.forEach(group => collectKeys(group));
    onExpandedKeysChange(allKeys);
  };

  // Collapse all groups
  const collapseAll = () => {
    onExpandedKeysChange([]);
  };

  // Expand children of a group
  const expandChildrenOfGroup = (group: PermissionTargetGroup, parentPath: string[] = []) => {
    const allKeys: string[] = [...expandedKeys];
    const currentPath = [...parentPath, group.label];

    const collectKeys = (g: PermissionTargetGroup, path: string[]) => {
      const key = path.join('/');
      if (!allKeys.includes(key)) {
        allKeys.push(key);
      }

      if (g.children) {
        g.children.forEach(child => {
          if (isPermissionTargetGroup(child)) {
            collectKeys(child, [...path, child.label]);
          }
        });
      }
    };

    collectKeys(group, currentPath);
    onExpandedKeysChange(allKeys);
  };

  // Collapse children of a group
  const collapseChildrenOfGroup = (group: PermissionTargetGroup, parentPath: string[] = []) => {
    const currentPath = [...parentPath, group.label];
    const keysToRemove: string[] = [];

    const collectKeys = (g: PermissionTargetGroup, path: string[]) => {
      const key = path.join('/');
      keysToRemove.push(key);

      if (g.children) {
        g.children.forEach(child => {
          if (isPermissionTargetGroup(child)) {
            collectKeys(child, [...path, child.label]);
          }
        });
      }
    };

    collectKeys(group, currentPath);
    onExpandedKeysChange(expandedKeys.filter(k => !keysToRemove.includes(k)));
  };

  // Render permission target checkboxes recursively
  const renderTargetCheckboxes = (group: PermissionTargetGroup, parentPath: string[] = [], level: number = 0): React.ReactNode => {
    const currentPath = [...parentPath, group.label];
    const groupKey = currentPath.join('/');

    if (!group.children || group.children.length === 0) return null;

    const directTargets = group.children.filter(isPermissionTarget);
    const subGroups = group.children.filter(isPermissionTargetGroup);
    const selectedCount = countSelectedInGroup(group);

    // If this is a leaf group (only has direct targets, no subgroups)
    if (subGroups.length === 0) {
      return (
        <div key={groupKey} style={{ marginBottom: '4px' }}>
          <Collapse
            activeKey={expandedKeys.includes(groupKey) ? [groupKey] : []}
            onChange={() => {
              onExpandedKeysChange(
                expandedKeys.includes(groupKey) ? expandedKeys.filter(k => k !== groupKey) : [...expandedKeys, groupKey],
              );
            }}
            ghost
            style={{
              background: '#fafafa',
              borderRadius: '4px',
              border: '1px solid #e8e8e8',
            }}
          >
            <Collapse.Panel
              key={groupKey}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <strong style={{ fontSize: '13.5px', flex: 1 }}>{group.label}</strong>
                  {selectedCount > 0 && (
                    <span
                      style={{
                        background: '#1890ff',
                        color: 'white',
                        padding: '1px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        minWidth: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {selectedCount}
                    </span>
                  )}
                  <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                    <Tooltip title="Tümünü seç">
                      <Button
                        type="text"
                        size="small"
                        icon={<CheckSquareOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          selectAllInGroup(group);
                        }}
                        style={{ padding: '0 4px', height: '22px' }}
                      />
                    </Tooltip>
                    <Tooltip title="Seçimi kaldır">
                      <Button
                        type="text"
                        size="small"
                        icon={<MinusSquareOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          deselectAllInGroup(group);
                        }}
                        style={{ padding: '0 4px', height: '22px' }}
                      />
                    </Tooltip>
                  </div>
                </div>
              }
              style={{ borderBottom: 'none' }}
            >
              {directTargets.map((target: PermissionTarget) => (
                <div key={target.key} style={{ marginBottom: '6px', padding: '4px 8px', borderRadius: '4px', background: 'white' }}>
                  <Checkbox checked={isPermissionSelected(target.key)} onChange={e => handleTargetSelection(target, e.target.checked)} style={{display: 'flex', alignItems: 'center'}}>
                    <div style={{ fontSize: '12px', lineHeight: '1.4' }}>{target.label}</div>
                  </Checkbox>
                </div>
              ))}
            </Collapse.Panel>
          </Collapse>
        </div>
      );
    }

    // If this group has subgroups, render them with nested collapse

    return (
      <div key={groupKey} style={{ marginBottom: '4px' }}>
        <Collapse
          activeKey={expandedKeys.includes(groupKey) ? [groupKey] : []}
          onChange={() => {
            onExpandedKeysChange(expandedKeys.includes(groupKey) ? expandedKeys.filter(k => k !== groupKey) : [...expandedKeys, groupKey]);
          }}
          ghost={level > 0}
          bordered={level === 0}
          style={
            level === 0
              ? {
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #d9d9d9',
                  marginBottom: '8px',
                }
              : {
                  background: level === 1 ? '#fafafa' : '#f5f5f5',
                  borderRadius: '4px',
                  border: level === 1 ? '1px solid #e8e8e8' : '1px solid #f0f0f0',
                }
          }
        >
          <Collapse.Panel
            key={groupKey}
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <strong style={{ fontSize: '13.5px', flex: 1 }}>{group.label}</strong>
                {selectedCount > 0 && (
                  <span
                    style={{
                      background: '#1890ff',
                      color: 'white',
                      padding: '1px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      minWidth: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {selectedCount}
                  </span>
                )}
                <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                  <Tooltip title="Tümünü seç">
                    <Button
                      type="text"
                      size="small"
                      icon={<CheckSquareOutlined />}
                      onClick={e => {
                        e.stopPropagation();
                        selectAllInGroup(group);
                      }}
                      style={{ padding: '0 4px', height: '22px' }}
                    />
                  </Tooltip>
                  <Tooltip title="Seçimi kaldır">
                    <Button
                      type="text"
                      size="small"
                      icon={<MinusSquareOutlined />}
                      onClick={e => {
                        e.stopPropagation();
                        deselectAllInGroup(group);
                      }}
                      style={{ padding: '0 4px', height: '22px' }}
                    />
                  </Tooltip>
                  {subGroups.length > 0 && (
                    <>
                      <Tooltip title="Alt grupları genişlet">
                        <Button
                          type="text"
                          size="small"
                          icon={<ExpandOutlined />}
                          onClick={e => {
                            e.stopPropagation();
                            expandChildrenOfGroup(group, parentPath);
                          }}
                          style={{ padding: '0 4px', height: '22px' }}
                        />
                      </Tooltip>
                      <Tooltip title="Alt grupları daralt">
                        <Button
                          type="text"
                          size="small"
                          icon={<CompressOutlined />}
                          onClick={e => {
                            e.stopPropagation();
                            collapseChildrenOfGroup(group, parentPath);
                          }}
                          style={{ padding: '0 4px', height: '22px' }}
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            }
            style={{ borderBottom: 'none' }}
          >
            <div style={{ paddingLeft: level > 0 ? '12px' : '0' }}>
              {directTargets.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  {directTargets.map((target: PermissionTarget) => (
                    <div
                      key={target.key}
                      style={{ marginBottom: '6px', padding: '4px 8px', borderRadius: '4px', background: level > 0 ? 'white' : '#fafafa' }}
                    >
                      <Checkbox checked={isPermissionSelected(target.key)} onChange={e => handleTargetSelection(target, e.target.checked)}>
                        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>{target.label}</div>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              )}
              {subGroups.map((subGroup: PermissionTargetGroup) => renderTargetCheckboxes(subGroup, currentPath, level + 1))}
            </div>
          </Collapse.Panel>
        </Collapse>
      </div>
    );
  };

  return (
    <div>
      {/* Search and Controls Section */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '12px',
          background: '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
        }}
      >
        <Input
          placeholder="Ara..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={e => onSearchQueryChange(e.target.value)}
          allowClear
          style={{ maxWidth: '300px' }}
        />
        <Tooltip title="Tümünü genişlet">
          <Button size="small" type="text" icon={<ExpandOutlined />} onClick={expandAll} />
        </Tooltip>
        <Tooltip title="Tümünü daralt">
          <Button size="small" type="text" icon={<CompressOutlined />} onClick={collapseAll} />
        </Tooltip>
        <span
          style={{
            fontSize: '13px',
            color: '#595959',
            marginLeft: '4px',
          }}
        >
          {displayedPermissionCount} yetki eklendi
        </span>
      </div>

      {/* Target List */}
      <div style={{ maxHeight: '500px', overflow: 'auto' }}>
        {loadingPermissionTargets ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Yükleniyor...</div>
        ) : filteredPermissionTargets.length === 0 ? (
          <Empty description={searchQuery ? 'Arama sonucu bulunamadı' : 'Yetki hedefi bulunamadı'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          filteredPermissionTargets.map(group => renderTargetCheckboxes(group))
        )}
      </div>
    </div>
  );
}
